import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ShieldCheck,
  Compass,
  BookOpen,
  Palmtree,
  ArrowRight,
  Star,
  Sparkles,
  Eye,
  HeartHandshake,
} from "lucide-react";
import { SEO, SITE_URL } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { QUICK_LINKS, PACKAGES, TESTIMONIALS, SITE_LOGO } from "../data/content";
import { useContent } from "../lib/useContent";

const ICONS = { ShieldCheck, Compass, BookOpen, Palmtree };

export default function Home() {
  const { data: clubs } = useContent("/clubs");
  const { data: blogPosts } = useContent("/blog-posts");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const orb2Y = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Travel Club Navigator Group",
      url: SITE_URL,
      logo: SITE_LOGO,
      description:
        "Independent, consumer-first reviews of travel clubs and timeshares, plus membership guides and exclusive vacation packages.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Travel Club Navigator Group",
      url: SITE_URL,
    },
  ];
  return (
    <>
      <SEO
        description="Travel Club Navigator Group — honest timeshare & travel club reviews, membership guides, and exclusive vacation packages. Navigating your travel journey with transparency and zero stress."
        image="https://images.unsplash.com/photo-1769149255670-aa0ad6428dd6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex min-h-[92vh] items-end overflow-hidden"
        style={{ perspective: "1200px" }}
      >
        {/* Parallax background image with continuous Ken Burns drift */}
        <motion.div
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 -top-[8%] h-[116%] w-full will-change-transform"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1672812407183-001d3533f49a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Overwater villas scattered across a vivid turquoise lagoon at an exclusive tropical resort"
            className="h-full w-full object-cover will-change-transform"
            initial={{ scale: 1.15, opacity: 0 }}
            animate={{
              scale: [1.15, 1.08, 1.15],
              x: ["0%", "-2.5%", "0%"],
              y: ["0%", "-1.8%", "0%"],
              opacity: 1,
            }}
            transition={{
              opacity: { duration: 1.4, ease: "easeOut" },
              scale: { duration: 26, ease: "easeInOut", repeat: Infinity },
              x: { duration: 26, ease: "easeInOut", repeat: Infinity },
              y: { duration: 26, ease: "easeInOut", repeat: Infinity },
            }}
          />
        </motion.div>
        <div className="hero-scrim absolute inset-0" />

        {/* Floating depth layers (3D parallax) */}
        <motion.div
          aria-hidden
          style={{ y: orbY }}
          className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-accent/30 blur-3xl"
        />
        <motion.div
          aria-hidden
          style={{ y: orb2Y }}
          className="pointer-events-none absolute -left-16 bottom-10 h-80 w-80 rounded-full bg-primary/25 blur-3xl"
        />

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-32 lg:px-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-sm font-semibold text-white backdrop-blur-md">
              <Sparkles className="h-4 w-4 text-accent" />
              The consumer advocate for travel
            </span>
            <h1 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
              Navigating your travel journey with{" "}
              <span className="text-accent">transparency</span> and{" "}
              <span className="text-primary">zero stress</span>.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-200">
              Honest reviews of travel clubs and timeshare presentations, no-nonsense
              membership guides, and hand-picked escapes to the world's most exclusive
              destinations.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/packages"
                data-testid="hero-packages-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105"
              >
                Explore Packages
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/travel-club-reviews"
                data-testid="hero-reviews-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-7 py-4 text-base font-semibold text-white backdrop-blur-md transition-colors duration-200 hover:bg-white/20"
              >
                Read the Reviews
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-10 sm:grid-cols-3 lg:px-8">
          {[
            { icon: Eye, title: "Radical Transparency", text: "Every fee, tactic, and catch — named and explained." },
            { icon: ShieldCheck, title: "Consumer-First", text: "We work for travelers, never for the sellers." },
            { icon: HeartHandshake, title: "Zero-Pressure", text: "Inquire, learn, and book on your own terms." },
          ].map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="flex items-start gap-4">
              <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-secondary/10 text-secondary">
                <v.icon className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-navy">{v.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUICK LINKS BENTO */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>Start Here</Overline>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            Four clear paths to smarter, stress-free travel.
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-12">
          {QUICK_LINKS.map((link, i) => {
            const Icon = ICONS[link.icon];
            const featured = i === 0;
            return (
              <Reveal
                key={link.to}
                delay={i * 0.07}
                className={featured ? "md:col-span-8" : "md:col-span-4"}
              >
                <Link
                  to={link.to}
                  data-testid={`quicklink-${link.to.replace("/", "")}`}
                  className="group relative block h-full min-h-[280px] overflow-hidden rounded-3xl card-3d"
                >
                  <img
                    src={link.image}
                    alt={link.alt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
                  <div className="relative flex h-full flex-col justify-end p-7">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-md">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-bold text-white">
                      {link.title}
                    </h3>
                    <p className="mt-2 max-w-md text-sm text-slate-200">{link.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                      Explore
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CLUB REVIEWS PREVIEW */}
      <section className="bg-muted section-pad">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Overline>Travel Club Reviews</Overline>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                We name names. All four, in full.
              </h2>
            </div>
            <Link
              to="/travel-club-reviews"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              View all reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(clubs || []).map((club, i) => (
              <Reveal key={club.slug} delay={i * 0.06}>
                <Link
                  to={`/travel-club-reviews/${club.slug}`}
                  data-testid={`club-preview-${club.slug}`}
                  className="group block h-full overflow-hidden rounded-3xl card-3d"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={club.image}
                      alt={club.alt}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-navy backdrop-blur">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                      {club.rating.toFixed(1)}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-navy">{club.name}</h3>
                    {club.partner && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-navy">
                        <Star className="h-3 w-3 fill-accent text-accent" /> Featured Partner
                      </span>
                    )}
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-secondary">
                      {club.verdict}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-500">
                      {club.tagline}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEW MEMBERS BAND */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="grid overflow-hidden rounded-[2rem] card-3d lg:grid-cols-2">
            <div className="relative min-h-[280px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1603423697311-cfdc31203b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="New travel club member relaxing in an infinity pool overlooking a green hillside"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <Overline>Just Bought In?</Overline>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                Confused about your new membership? Start here.
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Timeshare and travel club paperwork is dense — and nobody explains the next
                step. Our plain-English roadmap takes new owners from "what did I buy?" to
                booking their first dream trip in 30 days.
              </p>
              <Link
                to="/members"
                data-testid="home-members-cta"
                className="group mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-secondary px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-105"
              >
                Open the New Member Guide
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PACKAGES PREVIEW */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Overline>Vacation Packages</Overline>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              Exclusive escapes, curated for you.
            </h2>
          </div>
          <Link
            to="/packages"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Browse the store <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {PACKAGES.slice(0, 3).map((pkg, i) => (
            <Reveal key={pkg.id} delay={i * 0.08}>
              <Link
                to="/packages"
                className="group block h-full overflow-hidden rounded-3xl card-3d"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.alt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute right-4 top-4 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                    {pkg.tag}
                  </span>
                </div>
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    {pkg.destination}
                  </p>
                  <h3 className="mt-1.5 font-display text-xl font-bold text-navy">
                    {pkg.name}
                  </h3>
                  <div className="mt-4 flex items-end justify-between">
                    <span className="text-sm text-slate-500">{pkg.nights} nights</span>
                    <span className="font-display text-lg font-bold text-navy">
                      From ${pkg.priceFrom.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="bg-muted section-pad">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Overline>From The Blog</Overline>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                Ideas for your next escape.
              </h2>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Read the blog <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {(blogPosts || []).slice(0, 3).map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`home-blog-${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl card-3d"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.alt}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy backdrop-blur">
                      {post.sponsored ? "Sponsored" : post.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-bold text-navy">{post.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50 section-pad" data-testid="home-testimonials">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline className="text-primary">Traveler Stories</Overline>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-black sm:text-4xl">
              Real people. Honest outcomes.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure className="flex h-full flex-col rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
                  <blockquote className="font-serif-accent text-lg italic leading-relaxed text-black">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3">
                    <img
                      src={t.image}
                      alt={t.alt}
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-primary/60"
                    />
                    <div>
                      <p className="font-semibold text-black">{t.name}</p>
                      <p className="text-sm text-slate-600">{t.location}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER BAND */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center justify-between gap-6 rounded-[2rem] border border-slate-100 bg-white p-8 shadow-sm sm:flex-row sm:p-10">
            <div>
              <Overline>For Travel Clubs & Resorts</Overline>
              <h2 className="mt-2 font-display text-2xl font-bold text-navy sm:text-3xl">
                Reach travelers who are ready to book.
              </h2>
              <p className="mt-2 max-w-xl text-slate-600">
                Feature your brand with clearly-labeled, high-converting sponsored content in
                front of 250K+ high-intent readers every month.
              </p>
            </div>
            <Link
              to="/partners"
              data-testid="home-partner-cta"
              className="inline-flex flex-none items-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:scale-105"
            >
              Partner With Us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* CTA BANNER */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] card-3d">
            <img
              src="https://images.unsplash.com/photo-1692417045879-8ec84e750bb5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
              alt="Overwater swing bed at sunset at a tropical luxury resort"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
            <div className="relative px-8 py-16 sm:px-14 sm:py-20">
              <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to travel smarter — without the pressure?
              </h2>
              <p className="mt-4 max-w-xl text-slate-200">
                Send a quick, no-obligation inquiry and our team will craft a transparent
                plan for your next exclusive escape.
              </p>
              <Link
                to="/packages"
                data-testid="cta-banner-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105"
              >
                Start Your Inquiry <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
